import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Share2,
  Tag,
  Quote,
} from "lucide-react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  formatBlogDate,
  getBlogPostBySlug,
  getRelatedPosts,
} from "@/constants/blog";
import { toast } from "sonner";

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getBlogPostBySlug(slug) : undefined;

  const relatedPosts = useMemo(() => {
    if (!slug) return [];
    return getRelatedPosts(slug, 3);
  }, [slug]);

  if (!article) {
    return <Navigate to="/blog" replace data-oid="bg-article-redirect" />;
  }

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: article.title,
      text: article.excerpt,
      url,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Article shared");
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (error) {
      toast.error("Unable to share this article right now");
      console.error(error);
    }
  };

  return (
    <Wrapper className="py-16 md:py-24" data-oid="blog-article-wrapper">
      <Container className="max-w-5xl mx-auto" data-oid="blog-article-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
          data-oid="blog-article-breadcrumb"
        >
          <Link
            to="/blog"
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </motion.div>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="space-y-8"
          data-oid="blog-article-hero"
        >
          <div className="space-y-6">
            <Badge>{article.category}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              {article.title}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl">
              {article.excerpt}
            </p>

            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-14 h-14 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div>
                  <p className="text-base font-semibold">{article.author.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {article.author.role}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatBlogDate(article.publishedAt)}
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {article.readTime}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="gap-2" onClick={handleShare}>
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button asChild className="gap-2">
                  <a href="#key-takeaways">
                    <Tag className="w-4 h-4" />
                    Key Takeaways
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border bg-card">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-[420px] object-cover"
              onError={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #3b82f6, #8b5cf6)";
                e.currentTarget.style.height = "420px";
                e.currentTarget.src = "";
              }}
            />
          </div>
        </motion.article>

        <div className="mt-14 space-y-16">
          {article.sections.map((section, index) => (
            <motion.section
              key={section.heading}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="space-y-4"
              data-oid={`blog-article-section-${index}`}
            >
              <h2 className="text-2xl md:text-3xl font-semibold">
                {section.heading}
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-lg">
                {section.body.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex}>{paragraph}</p>
                ))}
              </div>
            </motion.section>
          ))}

          {article.highlights && (
            <section data-oid="blog-article-highlights">
              <div className="mb-6">
                <p className="text-sm font-semibold text-blue-500 uppercase">
                  Quick stats
                </p>
                <h3 className="text-2xl font-semibold">Highlights</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {article.highlights.map((highlight) => (
                  <div
                    key={highlight.title}
                    className="p-6 rounded-2xl border bg-card"
                  >
                    <p className="text-3xl font-bold mb-2">{highlight.title}</p>
                    <p className="text-muted-foreground text-sm">
                      {highlight.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {article.quote && (
            <section className="py-10" data-oid="blog-article-quote">
              <div className="rounded-3xl bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/20 p-8 relative overflow-hidden">
                <Quote className="w-16 h-16 text-blue-500 opacity-30 absolute -top-4 -right-2" />
                <p className="text-2xl md:text-3xl font-semibold text-foreground mb-6">
                  “{article.quote.text}”
                </p>
                <div>
                  <p className="font-semibold">{article.quote.author}</p>
                  <p className="text-sm text-muted-foreground">{article.quote.role}</p>
                </div>
              </div>
            </section>
          )}

          <section id="key-takeaways" data-oid="blog-article-takeaways">
            <div className="mb-6">
              <p className="uppercase text-xs font-semibold text-blue-500">
                Summary
              </p>
              <h3 className="text-2xl font-semibold">Key Takeaways</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {article.keyTakeaways.map((takeaway, index) => (
                <div
                  key={takeaway}
                  className="p-5 rounded-2xl border bg-card flex gap-4"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-muted-foreground">{takeaway}</p>
                </div>
              ))}
            </div>
          </section>

          {article.resources && article.resources.length > 0 && (
            <section data-oid="blog-article-resources">
              <h3 className="text-2xl font-semibold mb-4">Resources</h3>
              <div className="flex flex-col gap-3">
                {article.resources.map((resource) => (
                  <a
                    key={resource.href}
                    href={resource.href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-2xl border bg-card px-5 py-4 hover:border-blue-500 transition-colors"
                  >
                    <div>
                      <p className="font-semibold">{resource.label}</p>
                      <p className="text-sm text-muted-foreground">
                        {resource.href}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <section className="mt-20" data-oid="blog-article-related">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm uppercase font-semibold text-blue-500">
                Keep reading
              </p>
              <h3 className="text-2xl font-semibold">Related Articles</h3>
            </div>
            <Link
              to="/blog"
              className="text-sm font-semibold text-blue-500 hover:text-blue-600"
            >
              View all
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {relatedPosts.map((post) => (
              <article
                key={post.slug}
                className="rounded-2xl border bg-card overflow-hidden flex flex-col"
              >
                <Link to={`/blog/${post.slug}`}>
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-44 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #3b82f6, #8b5cf6)";
                      e.currentTarget.style.height = "176px";
                      e.currentTarget.src = "";
                    }}
                  />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <Badge variant="secondary" className="w-fit mb-3">
                    {post.category}
                  </Badge>
                  <Link to={`/blog/${post.slug}`} className="flex-1">
                    <h4 className="text-lg font-semibold leading-snug line-clamp-2">
                      {post.title}
                    </h4>
                  </Link>
                  <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{formatBlogDate(post.publishedAt)}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </Container>
    </Wrapper>
  );
};

export default BlogArticlePage;
